import { ReactElement, useEffect, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { ContentLabelField } from '@hopskipdrive/react-components';
import * as Yup from 'yup';

import RiderDriverSearch from '../../../components/RiderDriverSearch';
import { IncidentIndividual } from '../../../types/incidents';

interface ImpactedUserIdsFieldProps {
  defaultValue?: (number | undefined)[];
  currentOffenderType: string;
  riders: IncidentIndividual[];
  drivers: IncidentIndividual[];
  carePartnerDrivers?: IncidentIndividual[];
  riderAssistants?: IncidentIndividual[];
}

export const impactedUserIdsValidation = Yup.array()
  .of(Yup.number())
  .required('Affected parties is required')
  .min(1, 'Select at least one affected party')
  .nullable();

export const ImpactedUserIdsField = ({
  defaultValue,
  currentOffenderType,
  riders,
  drivers,
  carePartnerDrivers,
  riderAssistants,
}: ImpactedUserIdsFieldProps): ReactElement => {
  const [options, setOptions] = useState<IncidentIndividual[]>([]);
  const previousOffenderType = useRef(currentOffenderType);
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name: 'impactedUserIds',
    control,
    defaultValue,
  });
  const setAffectedUsers = (offenderType: string) => affectedUsersMap[offenderType.toLowerCase()] || [];

  useEffect(() => {
    setOptions(setAffectedUsers(currentOffenderType?.toLowerCase()) || []);
  }, [currentOffenderType, drivers, riders]);

  useEffect(() => {
    if (previousOffenderType.current && previousOffenderType.current !== currentOffenderType) {
      previousOffenderType.current = currentOffenderType;
      field.onChange(null);
    }
  }, [currentOffenderType]);

  const affectedUsersMap = {
    driver: [...(riders || []), ...(riderAssistants || [])],
    carepartnerdriver: [...(riders || []), ...(riderAssistants || [])],
    passenger: [...(drivers || []), ...(carePartnerDrivers || []), ...(riderAssistants || [])],
    riderassistant: [...(drivers || []), ...(carePartnerDrivers || []), ...(riders || [])],
  };

  return (
    <ContentLabelField
      id="impactedUserIds"
      title={'Affected User(s)'}
      editMode={true}
      required={true}
      editView={
        <RiderDriverSearch
          options={options}
          value={options.filter((item) => field.value?.includes(item.id)) || null}
          onChange={(selected) => {
            Array.isArray(selected) ? field.onChange(selected.map((item) => item.id)) : field.onChange(null);
          }}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          placeholder={'Search or select User(s)'}
          multiple={true}
        />
      }
    />
  );
};
